import React, { useEffect, useState } from 'react';
import { ShoppingBag } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CartDrawerHeader } from './CartDrawerHeader';
import { FreeShippingProgressBar } from './FreeShippingProgressBar';
import { CartLineItem } from './CartLineItem';
import { CartDrawerFooter } from './CartDrawerFooter';
import { DiscountPanel } from './DiscountPanel';
import { t } from '../../constants/i18n';
import { NativeDrawer } from '@/components/primitives/NativeDrawer';
import { useCartBootstrap } from '@/hooks/cart/useCartBootstrap';
import { useCartDrawer } from '@/hooks/cart/useCartDrawer';
import { LINE_ITEM_SWIPE_EVENT } from '@/events/cartLineItemEvents';

export const CartDrawer: React.FC = () => {
    const {
        isCartOpen,
        itemLineRefs,
        count,
        currency,
        freeShipping,
        isDiscountPanelOpen,
        toggleCart,
        toggleDiscountPanel,
        closeCart
    } = useCartDrawer();
    const [openLineRef, setOpenLineRef] = useState<string | null>(null);

    // 用 Hook 内的 Service 调用替代 UI 直连
    useCartBootstrap(isCartOpen);

    useEffect(() => {
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<{ lineRef?: string | null }>).detail;
            const nextLineRef = detail?.lineRef ?? null;
            setOpenLineRef((prev) => (prev === nextLineRef ? prev : nextLineRef));
        };
        window.addEventListener(LINE_ITEM_SWIPE_EVENT, handler);
        return () => window.removeEventListener(LINE_ITEM_SWIPE_EVENT, handler);
    }, []);

    useEffect(() => {
        if (!isCartOpen && openLineRef) {
            setOpenLineRef(null);
        }
    }, [isCartOpen, openLineRef]);

    useEffect(() => {
        if (openLineRef && !itemLineRefs.includes(openLineRef)) {
            setOpenLineRef(null);
        }
    }, [itemLineRefs, openLineRef]);

    return (
        <div className="relative z-9999 font-sans select-none">
            <style>
                {`
          input[type="number"]::-webkit-outer-spin-button,
          input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type="number"] {
            -moz-appearance: textfield;
          }
        `}
            </style>

            <NativeDrawer
                isOpen={isCartOpen}
                onClose={closeCart}
                panelClassName="font-sans"
                transition={{
                    type: 'spring',
                    damping: 25,
                    stiffness: 200
                }}
            >
                {/* Header */}
                <CartDrawerHeader
                    count={count}
                    onClose={closeCart}
                />

                {/* Shipping Progress */}
                {count > 0 && (
                    <FreeShippingProgressBar
                        progressPercent={freeShipping.progressPercent}
                        remainingCents={freeShipping.remaining}
                        currency={currency || 'EUR'}
                    />
                )}

                {/* Body */}
                {count === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground space-y-4 p-6">
                        <ShoppingBag size={72} strokeWidth={1} />
                        <p className="text-base font-medium">{t('emptyCartTitle')}</p>
                    </div>
                ) : (
                    <ScrollArea className="flex-1 overscroll-contain">
                        <div className="px-6 pt-2 pb-6 space-y-0">
                            <AnimatePresence initial={false}>
                                {itemLineRefs.map((lineRef) => (
                                    <CartLineItem
                                        key={lineRef}
                                        lineRef={lineRef}
                                        isOpen={openLineRef === lineRef}
                                    />
                                ))}
                            </AnimatePresence>

                            <CartDrawerFooter
                                setIsOpen={toggleCart}
                                toggleDiscountPanel={toggleDiscountPanel}
                            />
                        </div>
                    </ScrollArea>
                )}
            </NativeDrawer>

            {/* Discount Panel 提升为独立交互层，覆盖 Drawer 内容，透明遮罩点击关闭 */}
            {/* Discount Panel 提升为独立交互层，覆盖 Drawer 内容，由组件内部控制 AnimatePresence */}
            <DiscountPanel
                isOpen={isDiscountPanelOpen}
                onClose={() => toggleDiscountPanel(false)}
            />
        </div>
    );
};
